import React, { useEffect, useState } from "react";
import { X, Check, Circle, Clock, XCircle } from "lucide-react";
import { api, formatCrypto, formatFiat } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import IdVerificationPanel from "@/components/IdVerificationPanel";

/**
 * Modal showing the 14-step withdrawal pipeline for a given transaction.
 * Polls every 5s so the user sees admin advances in near-real-time.
 */
export default function WithdrawalProgressModal({ txId, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!txId) return;
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await api.get(`/withdrawals/${txId}`);
        if (alive) setData(r.data);
      } catch (e) {
        // Poll retry every 5s (interval below) — log so it's visible in devtools.
        // eslint-disable-next-line no-console
        console.error("WithdrawalProgressModal fetch failed", e?.response?.status || e?.message);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [txId]);

  const refresh = async () => {
    if (!txId) return;
    try {
      const r = await api.get(`/withdrawals/${txId}`);
      setData(r.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("WithdrawalProgressModal refresh failed", e?.response?.status || e?.message);
    }
  };

  if (!txId) return null;

  const tx = data?.transaction;
  const steps = data?.steps || [];
  const history = data?.history || [];
  const current = data?.current_step || 1;
  const isRejected = tx?.status === "rejected";
  const isComplete = tx?.status === "completed";

  const historyByStep = Object.fromEntries(history.map((h) => [h.step, h]));

  return (
    <div
      data-testid="withdraw-progress-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0B0D12] border border-[#2A2E38] w-full max-w-2xl my-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#141720] border-b border-[#2A2E38] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#7C5CFF]">
              {t("wprog.eyebrow")}
            </div>
            <h2 className="font-display text-xl font-bold mt-1">
              {t("wprog.title")}
            </h2>
          </div>
          <button
            data-testid="withdraw-progress-close"
            onClick={onClose}
            className="p-2 text-[#A3A9B8] hover:text-[#F4F1EB]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Summary */}
        {tx && (
          <div className="px-6 py-4 border-b border-[#2A2E38] grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wprog.amount")}</div>
              <div className="font-mono mt-1">
                {tx.kind === "fiat" ? formatFiat(tx.amount, tx.asset) : `${formatCrypto(tx.amount)} ${tx.asset}`}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wprog.fee")}</div>
              <div className="font-mono mt-1">
                {tx.fee_percent}% · {Number(tx.fee_amount || 0).toFixed(8)}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wprog.progress")}</div>
              <div className="font-mono mt-1">
                {current} / {data?.total || steps.length || 14}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wprog.status")}</div>
              <div className={`font-mono mt-1 ${
                isRejected ? "text-[#C75C5C]" : isComplete ? "text-[#7C9C69]" : "text-[#D19B54]"
              }`}>
                {isRejected ? t("wprog.st_rejected") : isComplete ? t("wprog.st_complete") : t("wprog.st_pending")}
              </div>
            </div>
          </div>
        )}

        {isRejected && (
          <div className="mx-6 my-4 p-4 border border-[#C75C5C]/40 bg-[#C75C5C]/5 text-sm">
            <div className="flex items-start gap-2 text-[#C75C5C]">
              <XCircle size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{t("wprog.rejected_title")}</div>
                <div className="text-xs text-[#A3A9B8] mt-1">
                  {t("wprog.rejected_text", {
                    principal: Number(tx?.amount || 0).toFixed(8),
                    fee: Number(tx?.fee_refunded || tx?.fee_amount || 0).toFixed(8),
                    asset: tx?.asset,
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ID verification (driver's license) — required at step 3.
            Show for end-user only; admin gets a dedicated review panel from the
            Admin console. */}
        {!isAdmin && !isRejected && tx && current >= 3 && tx.id_verification && tx.id_verification.status !== "rejected_final" && (
          <IdVerificationPanel
            txId={txId}
            idv={tx.id_verification}
            onUploaded={refresh}
          />
        )}

        {/* Timeline */}
        <div className="px-6 py-6">
          {loading && <div className="text-center text-sm text-[#6E7587]">{t("wprog.loading")}</div>}
          {!loading && steps.length > 0 && (
            <ol className="relative space-y-0">
              {steps.map((s, idx) => {
                const stepHistory = historyByStep[s.index];
                const done = s.index < current || isComplete;
                const active = s.index === current && !isComplete && !isRejected;
                const future = s.index > current;
                const isLast = idx === steps.length - 1;
                return (
                  <li key={s.key} className="flex gap-4 pb-6 relative">
                    {/* Connector line */}
                    {!isLast && (
                      <span
                        className={`absolute left-[15px] top-8 w-px h-full -ml-px ${
                          done && !isRejected ? "bg-[#7C9C69]" : isRejected && s.index < current ? "bg-[#C75C5C]/60" : "bg-[#2A2E38]"
                        }`}
                      />
                    )}
                    {/* Dot / icon */}
                    <div
                      className={`relative z-10 w-8 h-8 rounded-full grid place-items-center shrink-0 border-2 ${
                        done && !isRejected
                          ? "bg-[#7C9C69]/15 border-[#7C9C69] text-[#7C9C69]"
                          : active
                          ? "bg-[#7C5CFF]/15 border-[#7C5CFF] text-[#7C5CFF] animate-pulse"
                          : isRejected && s.index <= current
                          ? "bg-[#C75C5C]/10 border-[#C75C5C]/60 text-[#C75C5C]/80"
                          : "bg-[#0B0D12] border-[#2A2E38] text-[#6E7587]"
                      }`}
                    >
                      {done && !isRejected ? (
                        <Check size={14} strokeWidth={3} />
                      ) : active ? (
                        <Clock size={14} />
                      ) : isRejected && s.index <= current ? (
                        <XCircle size={14} />
                      ) : (
                        <Circle size={10} />
                      )}
                    </div>
                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={`text-[10px] font-mono tracking-wider ${
                          done && !isRejected ? "text-[#7C9C69]" : active ? "text-[#7C5CFF]" : "text-[#6E7587]"
                        }`}>
                          {String(s.index).padStart(2, "0")}
                        </span>
                        <span className={`text-sm font-medium ${
                          done || active ? "text-[#F4F1EB]" : "text-[#6E7587]"
                        }`}>
                          {s.label}
                        </span>
                      </div>
                      {stepHistory?.at && (
                        <div className="text-[10px] text-[#6E7587] font-mono mt-0.5">
                          {new Date(stepHistory.at).toLocaleString()}
                        </div>
                      )}
                      {stepHistory?.note && (
                        <div className="text-xs text-[#A3A9B8] mt-1 italic">&ldquo;{stepHistory.note}&rdquo;</div>
                      )}
                      {active && (
                        <div className="text-xs text-[#7C5CFF] mt-1">
                          {t("wprog.in_progress")}
                        </div>
                      )}
                      {future && !isRejected && (
                        <div className="text-xs text-[#6E7587] mt-1">
                          {t("wprog.awaiting")}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
